import { Fragment } from "react";
import { Link } from "react-router-dom";

import type { DashboardMatchBrief, DashboardMatchParticipantBrief, RetoSender } from "../lib/api";
import {
  firstName,
  guardianPhoneLabel,
  isTeamSide,
  RESULT_BY_ORGANIZER_NOTE,
  retoContactLine,
  retoRivalContacts,
  retoWhatsappMessage,
  whatsappHref,
  type RetoContact,
} from "../lib/retoContacto";
import Icon from "./icons";

type RetoActionsProps = {
  match: DashboardMatchBrief;
  rival: DashboardMatchParticipantBrief | null;
  /** Quien escribe el WhatsApp: nombre y apellido del perfil PROPIO elegido
   * (nunca el display_name ni la etiqueta de la pareja). */
  sender?: RetoSender | null;
  tournamentName?: string | null;
  reportHref: string;
  className: string;
  /** Escribe el celular (el inicio del torneo ya lo dice en su nota). */
  showContactLine?: boolean;
  /** «card»: el reto del inicio del torneo (Eduardo 2026-10-05, maqueta
   * aprobada): recuadro «Coordina directo con tu rival» con el celular
   * grande, WhatsApp y Llamar lado a lado y «Reportar resultado» a lo ancho,
   * con íconos de línea en lugar de emojis. Sin la prop, como siempre (home
   * del jugador). */
  variant?: "inline" | "card";
};

function contactMessage(
  contact: RetoContact,
  rival: DashboardMatchParticipantBrief | null,
  team: boolean,
  sender?: RetoSender | null,
  sport?: string | null,
) {
  const greeting = team ? contact.name : rival?.display_name ?? contact.name;
  const message = retoWhatsappMessage(sender, sport);
  const title = contact.guardian ? guardianPhoneLabel(greeting) : undefined;
  return { message, title };
}

/** «Coordina directo con tu rival» y el celular en grande (o los de la pareja
 * rival, cada uno con su nombre). El rival menor sin celular propio dice de
 * quién es el número (papá, mamá o tutor). Mismo contenido que
 * `retoContactLine`, acomodado en renglones. */
export function PlayerRetoContact({ rival }: { rival: DashboardMatchParticipantBrief | null }) {
  const contacts = retoRivalContacts(rival);
  const team = isTeamSide(rival);
  if (contacts.length === 0) {
    return (
      <div className="player-reto-card__contact" data-no-toast>
        <span className="player-reto-card__contact-label">{retoContactLine(rival)}</span>
      </div>
    );
  }
  if (team) {
    return (
      <div className="player-reto-card__contact" data-no-toast>
        <span className="player-reto-card__contact-label">Coordina directo con tus rivales</span>
        <ul className="player-reto-card__phones">
          {contacts.map((contact) => (
            <li key={contact.digits}>
              <span className="player-reto-card__phone-name">
                {contact.guardian ? guardianPhoneLabel(firstName(contact.name)) : firstName(contact.name)}
              </span>
              <span className="player-reto-card__phone">{contact.phone}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  const [contact] = contacts;
  return (
    <div className="player-reto-card__contact" data-no-toast>
      <span className="player-reto-card__contact-label">Coordina directo con tu rival</span>
      {contact.guardian ? (
        <span className="player-reto-card__contact-guardian">
          {guardianPhoneLabel(contact.name ?? rival?.display_name)}
        </span>
      ) : null}
      <span className="player-reto-card__phone">{contact.phone}</span>
    </div>
  );
}

/** Acciones de «Tu próximo reto» (inicio del torneo y home del jugador):
 * WhatsApp y Llamar a cada celular del rival que mande el servidor (pareja:
 * los dos) y «Reportar resultado», o el aviso de que lo captura el
 * organizador. Quien la usa decide cuándo se muestra (`retoShowsContact`).
 * Un rival menor sin celular propio trae el de su papá, mamá o tutor: el
 * mensaje de WhatsApp no saluda al niño (Eduardo 2026-10-04). */
export default function PlayerRetoActions({
  match,
  rival,
  sender,
  reportHref,
  className,
  showContactLine = false,
  variant = "inline",
}: RetoActionsProps) {
  const contacts = retoRivalContacts(rival);
  const team = isTeamSide(rival);
  const reporting = Boolean(match.allow_player_result_reporting);

  if (variant === "card") {
    const hasCoordination = showContactLine || contacts.length > 0;
    return (
      <div className={`${className} player-reto-card`} role="group" aria-label="Acciones del próximo reto">
        {hasCoordination ? (
          <div className={`player-reto-card__coord${showContactLine ? " has-contact" : ""}`}>
            {showContactLine ? <PlayerRetoContact rival={rival} /> : null}
            {contacts.length > 0 ? (
              <div className={`player-reto-card__buttons${contacts.length > 1 ? " is-team" : ""}`}>
                {contacts.map((contact) => {
                  const suffix = team ? ` ${firstName(contact.name)}` : "";
                  const { message, title } = contactMessage(contact, rival, team, sender, match.tournament_sport);
                  return (
                    <Fragment key={contact.digits}>
                      <a
                        className="player-reto-card__button player-reto-card__button--whatsapp"
                        href={whatsappHref(contact.digits, message)}
                        target="_blank"
                        rel="noreferrer"
                        title={title}
                      >
                        <Icon name="chat" />
                        <span>WhatsApp{suffix}</span>
                      </a>
                      <a
                        className="player-reto-card__button player-reto-card__button--call"
                        href={`tel:${contact.digits}`}
                        title={title}
                      >
                        <Icon name="phone" />
                        <span>Llamar{suffix}</span>
                      </a>
                    </Fragment>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
        {reporting ? (
          <Link className="player-reto-card__report" to={reportHref}>
            <Icon name="clipboard-check" />
            <span>Reportar resultado</span>
          </Link>
        ) : (
          <p className="player-reto-card__note">{RESULT_BY_ORGANIZER_NOTE}</p>
        )}
      </div>
    );
  }

  return (
    <div className={`${className} player-reto-actions`} aria-label="Acciones del próximo reto">
      {showContactLine && contacts.length > 0 ? (
        <span className="player-reto-actions__line">{retoContactLine(rival)}</span>
      ) : null}
      {contacts.map((contact) => {
        const suffix = team ? ` ${firstName(contact.name)}` : "";
        const { message, title } = contactMessage(contact, rival, team, sender, match.tournament_sport);
        return (
          <Fragment key={contact.digits}>
            <a
              className="player-tournament-home__action player-tournament-home__action--primary"
              href={whatsappHref(contact.digits, message)}
              target="_blank"
              rel="noreferrer"
              title={title}
            >
              💬 WhatsApp{suffix}
            </a>
            <a
              className="player-tournament-home__action player-tournament-home__action--primary"
              href={`tel:${contact.digits}`}
              title={title}
            >
              📞 Llamar{suffix}
            </a>
          </Fragment>
        );
      })}
      {reporting ? (
        <Link className="player-tournament-home__action player-tournament-home__action--primary" to={reportHref}>
          📋 Reportar resultado
        </Link>
      ) : (
        <span className="player-reto-actions__note">{RESULT_BY_ORGANIZER_NOTE}</span>
      )}
    </div>
  );
}
